import { getValidUuidOrNull } from "./duplicateUtils";

export interface AnswerReviewItem {
  questionId: string | number;
  questionNumber: number;
  title: string;
  instruction?: string;
  text?: string;
  userAnswer: string | null;
  correctAnswer: string | null;
  isAnswered: boolean;
  isCorrect: boolean;
  explanation?: string;
  options?: any;
  competence?: string;
  section?: string;
  subSection?: string;
}

export interface SectionAnalysis {
  section: string;
  total: number;
  correct: number;
  incorrect: number;
  unanswered: number;
  accuracy: number;
}

export interface PedagogicalAnalysis {
  totalQuestions: number;
  correctCount: number;
  incorrectCount: number;
  unansweredCount: number;
  accuracy: number;
  bySection: SectionAnalysis[];
  weakSections: string[];
  strongestSections: string[];
  incorrectItems: AnswerReviewItem[];
  unansweredItems: AnswerReviewItem[];
}

export interface PedagogicalRecommendation {
  title: string;
  description: string;
  priority: "high" | "medium" | "low";
}

function hasUserAnswer(item: any): boolean {
  if (!item) return false;
  const value =
    item.userAnswer ??
    item.selectedAnswer ??
    item.user_answer ??
    item.answer ??
    item.userOption ??
    item.selectedOption ??
    null;

  return value !== null && value !== undefined && String(value).trim() !== "";
}

export function analyzeAnswerReview(answerReview: AnswerReviewItem[] | null | undefined): PedagogicalAnalysis {
  if (!answerReview || !Array.isArray(answerReview) || answerReview.length === 0) {
    return {
      totalQuestions: 0,
      correctCount: 0,
      incorrectCount: 0,
      unansweredCount: 0,
      accuracy: 0,
      bySection: [],
      weakSections: [],
      strongestSections: [],
      incorrectItems: [],
      unansweredItems: []
    };
  }

  const totalQuestions = answerReview.length;
  let correctCount = 0;
  let incorrectCount = 0;
  let unansweredCount = 0;

  const incorrectItems: AnswerReviewItem[] = [];
  const unansweredItems: AnswerReviewItem[] = [];

  const sectionMap: Record<string, { total: number; correct: number; incorrect: number; unanswered: number }> = {};

  answerReview.forEach(item => {
    const isAnswered = item.isAnswered === true || hasUserAnswer(item);
    const isCorrect = item.isCorrect === true;

    if (isCorrect) {
      correctCount++;
    } else if (isAnswered) {
      incorrectCount++;
      incorrectItems.push(item);
    } else {
      unansweredCount++;
      unansweredItems.push(item);
    }

    let sectionName = item.section ? String(item.section).trim() : "";
    if (item.subSection) {
      if (item.subSection === "phrases_lacunaires") sectionName = "Phrases lacunaires";
      else if (item.subSection === "textes_lacunaires") sectionName = "Textes lacunaires";
      else if (item.subSection === "lecture_rapide_textes") sectionName = "Lecture rapide de textes";
      else if (item.subSection === "lecture_rapide_graphiques") sectionName = "Lecture rapide de graphiques";
    }

    const sectionKey = sectionName || "Sem seção definida";

    if (!sectionMap[sectionKey]) {
      sectionMap[sectionKey] = { total: 0, correct: 0, incorrect: 0, unanswered: 0 };
    }

    sectionMap[sectionKey].total++;
    if (isCorrect) {
      sectionMap[sectionKey].correct++;
    } else if (isAnswered) {
      sectionMap[sectionKey].incorrect++;
    } else {
      sectionMap[sectionKey].unanswered++;
    }
  });

  const accuracy = totalQuestions > 0 ? (correctCount / totalQuestions) * 100 : 0;

  const bySection: SectionAnalysis[] = Object.keys(sectionMap).map(sec => {
    const s = sectionMap[sec];
    const secAccuracy = s.total > 0 ? (s.correct / s.total) * 100 : 0;
    return {
      section: sec,
      total: s.total,
      correct: s.correct,
      incorrect: s.incorrect,
      unanswered: s.unanswered,
      accuracy: secAccuracy
    };
  });

  // Sort sections by accuracy ascending
  bySection.sort((a, b) => a.accuracy - b.accuracy);

  const weakSections = bySection.filter(s => s.accuracy < 60).map(s => s.section);
  const strongestSections = bySection.filter(s => s.accuracy >= 80).map(s => s.section);

  return {
    totalQuestions,
    correctCount,
    incorrectCount,
    unansweredCount,
    accuracy,
    bySection,
    weakSections,
    strongestSections,
    incorrectItems,
    unansweredItems
  };
}

export function getWeakSections(answerReview: AnswerReviewItem[] | null | undefined): string[] {
  const analysis = analyzeAnswerReview(answerReview);
  return analysis.weakSections;
}

export function generatePedagogicalRecommendations(analysis: PedagogicalAnalysis): PedagogicalRecommendation[] {
  if (analysis.totalQuestions === 0) return [];

  const recommendations: PedagogicalRecommendation[] = [];

  if (analysis.unansweredCount > 0) {
    recommendations.push({
      title: "Evite deixar questões em branco",
      description: `Você deixou ${analysis.unansweredCount} questão(ões) sem resposta. Em modo prova, tente marcar uma alternativa mesmo em caso de dúvida.`,
      priority: "medium"
    });
  }

  if (analysis.accuracy < 50) {
    recommendations.push({
      title: "Reforçar esta competência",
      description: "Seu aproveitamento ficou abaixo de 50%. Recomendamos revisar as questões erradas e criar flashcards.",
      priority: "high"
    });
  } else if (analysis.accuracy >= 80) {
    recommendations.push({
      title: "Bom desempenho geral",
      description: "Você demonstrou boa compreensão nesta prova. Continue praticando para manter consistência.",
      priority: "low"
    });
  }

  if (analysis.weakSections.length > 0) {
    recommendations.push({
      title: `Priorize a seção: ${analysis.weakSections[0]}`,
      description: "Essa foi uma das seções com menor aproveitamento neste teste.",
      priority: "high"
    });
  }

  if (analysis.incorrectCount > 0 && analysis.unansweredCount === 0) {
    recommendations.push({
      title: "Revise as questões incorretas",
      description: `Você errou ${analysis.incorrectCount} questão(ões). Revise a explicação e transforme esses erros em flashcards ou registros no Banco de Erros.`,
      priority: "medium"
    });
  } else if (analysis.incorrectCount > 0) {
    recommendations.push({
      title: "Transforme erros em revisão",
      description: "Salve suas questões erradas no Banco de Erros e crie flashcards para revisá-las depois.",
      priority: "medium"
    });
  }

  return recommendations.slice(0, 4);
}

export function getQuestionStudyActionPayload(
  reviewItem: AnswerReviewItem,
  optionsTextHelper: (options: any, answer: unknown) => string,
  competenceType: string,
  testAttemptId?: string | null
) {
  const uOptText = optionsTextHelper(reviewItem.options, reviewItem.userAnswer);
  const cOptText = optionsTextHelper(reviewItem.options, reviewItem.correctAnswer);

  const userAnswerStr = reviewItem.userAnswer 
    ? `Resposta do usuário: ${reviewItem.userAnswer}${uOptText ? ` - ${uOptText}` : ""}` 
    : "Questão não respondida.";

  const correctAnswerStr = `Resposta correta: ${reviewItem.correctAnswer || ""}${cOptText ? ` - ${cOptText}` : ""}`;

  const originalTextContent = reviewItem.text || reviewItem.title;
  const originalSentence = `${originalTextContent}\n\n${userAnswerStr}`;

  const comp = reviewItem.competence || competenceType;
  const tagType = comp.toLowerCase().includes("reading") || comp.toLowerCase().includes("ce") ? "CE" : "CO";

  const errorPayload = {
    original: originalSentence,
    correction: correctAnswerStr,
    explanation: reviewItem.explanation || "Explicação não disponível.",
    category: reviewItem.userAnswer ? "Erro de interpretação" : "Compreensão",
    competency: comp,
    section: reviewItem.section || "",
    tags: ["resultado", "questao-errada", tagType],
    metadata: {
      source: "test_result",
      questionId: reviewItem.questionId,
      questionNumber: reviewItem.questionNumber,
      title: reviewItem.title,
      userAnswer: reviewItem.userAnswer,
      correctAnswer: reviewItem.correctAnswer,
      options: reviewItem.options
    },
    duplicate_key: `result-error-${reviewItem.questionId}-${reviewItem.userAnswer || "blank"}-${reviewItem.correctAnswer || "unknown"}`
  };

  const flashcardPayload = {
    front: `Questão ${reviewItem.questionNumber}: ${reviewItem.title}\nSua resposta foi: ${reviewItem.userAnswer ? `${reviewItem.userAnswer}${uOptText ? ` - ${uOptText}` : ""}` : "Não respondida"}.\nQual era a resposta correta?`,
    back: `${correctAnswerStr}\n\nExplicação:\n${reviewItem.explanation || "Explicação não disponível."}`,
    type: "error",
    category: reviewItem.userAnswer ? "Erro de interpretação" : "Compreensão",
    language: "fr",
    source: "test_result",
    source_question_id: getValidUuidOrNull(String(reviewItem.questionId)),
    source_test_id: getValidUuidOrNull(testAttemptId || ""),
    tags: ["resultado", "questao-errada", tagType],
    metadata: {
      questionNumber: reviewItem.questionNumber,
      title: reviewItem.title,
      userAnswer: reviewItem.userAnswer,
      correctAnswer: reviewItem.correctAnswer,
      section: reviewItem.section,
      competence: comp
    },
    duplicate_key: `flashcard-result-${reviewItem.questionId}-${reviewItem.correctAnswer || "unknown"}`
  };

  return { errorPayload, flashcardPayload };
}
